import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { SiteFilter } from "@/components/admin/SiteFilter";
import {
  Search,
  AlertTriangle,
  Users,
  Shield,
  Network,
  ChevronDown,
  ChevronUp,
  Target,
  Activity,
  Sparkles,
  RefreshCw,
  Link2,
  TriangleAlert,
  Lightbulb,
  Eye,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useUserContext } from "@/hooks/useUserContext";
import { generateClusterSummary, safetyStateTranslations } from "@/utils/plainLanguageTranslations";

interface ClusterMember {
  id: string;
  display_name: string;
  current_risk_score: number;
  safety_state: string;
  network_role: string | null;
  is_wide_bridge: boolean;
}

interface ClusterDiagnostic {
  id: string;
  cluster_name: string | null;
  cluster_type: string | null;
  member_count: number;
  risk_score: number;
  redundancy_score: number;
  infected_count: number;
  site_id: string | null;
  site_name?: string;
  created_at: string;
  members: ClusterMember[];
  wide_bridges: ClusterMember[];
  key_risk_factors: string[];
  is_infected: boolean;
}

interface ClusterStats {
  totalClusters: number;
  infectedClusters: number;
  highRiskClusters: number;
  avgRiskScore: number;
  totalWideBridges: number;
}

interface ClusterDiagnosticsProps {
  siteId?: string | null;
}

const ClusterDiagnostics = ({ siteId: propSiteId }: ClusterDiagnosticsProps = {}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { companyId, isLoading: contextLoading } = useUserContext();
  const [clusters, setClusters] = useState<ClusterDiagnostic[]>([]);
  const [stats, setStats] = useState<ClusterStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSite, setSelectedSite] = useState(propSiteId || "all");
  const [stateFilter, setStateFilter] = useState<"all" | "infected" | "high_risk" | "healthy">("all");
  const [expandedCluster, setExpandedCluster] = useState<string | null>(null);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState<string | null>(null);
  const [generatingRecommendations, setGeneratingRecommendations] = useState(false);
  const [detectingClusters, setDetectingClusters] = useState(false);

  useEffect(() => {
    if (!contextLoading && companyId) {
      fetchClusterDiagnostics();
    }
  }, [selectedSite, contextLoading, companyId]);

  const fetchClusterDiagnostics = async () => {
    if (!companyId) return;
    
    setIsLoading(true);
    try {
      // Fetch clusters - use correct column names from DB schema
      let clustersQuery = supabase
        .from("worker_clusters")
        .select(`
          id, cluster_name, cluster_type, member_ids, cluster_risk_score, 
          redundancy_score, infected_member_count, is_infected, company_id, created_at
        `)
        .eq("company_id", companyId)
        .order("cluster_risk_score", { ascending: false });

      const { data: clustersData, error: clustersError } = await clustersQuery;
      if (clustersError) throw clustersError;

      // Collect all member IDs from all clusters
      const allMemberIds = new Set<string>();
      (clustersData || []).forEach((c: any) => {
        (c.member_ids || []).forEach((id: string) => allMemberIds.add(id));
      });

      // Fetch workers by member_ids (not cluster_id)
      const { data: workersData } = await supabase
        .from("worker_profiles")
        .select("id, display_name, current_risk_score, safety_state, network_role, is_wide_bridge, site_id")
        .in("id", Array.from(allMemberIds));

      // Create worker lookup map
      const workerMap: Record<string, any> = {};
      (workersData || []).forEach((w: any) => {
        workerMap[w.id] = {
          id: w.id,
          display_name: w.display_name || "Unknown",
          current_risk_score: w.current_risk_score || 0,
          safety_state: w.safety_state || "safe",
          network_role: w.network_role,
          is_wide_bridge: w.is_wide_bridge || false,
          site_id: w.site_id,
        };
      });

      // Group workers by cluster using member_ids
      const workersByCluster: Record<string, ClusterMember[]> = {};
      const siteByCluster: Record<string, string | null> = {};
      (clustersData || []).forEach((cluster: any) => {
        const members: ClusterMember[] = [];
        (cluster.member_ids || []).forEach((memberId: string) => {
          const worker = workerMap[memberId];
          if (worker) {
            members.push(worker);
            if (worker.site_id && !siteByCluster[cluster.id]) {
              siteByCluster[cluster.id] = worker.site_id;
            }
          }
        });
        workersByCluster[cluster.id] = members;
      });

      // Filter by site if needed
      let filteredClustersData = clustersData || [];
      if (selectedSite !== "all") {
        const clusterIdsInSite = Object.entries(siteByCluster)
          .filter(([_, siteId]) => siteId === selectedSite)
          .map(([clusterId]) => clusterId);
        filteredClustersData = filteredClustersData.filter((c: any) => clusterIdsInSite.includes(c.id));
      }

      // Transform clusters - map DB columns to our interface
      const diagnostics: ClusterDiagnostic[] = filteredClustersData.map((cluster: any) => {
        const members = workersByCluster[cluster.id] || [];
        const wideBridges = members.filter((m) => m.is_wide_bridge);
        const riskScore = cluster.cluster_risk_score || 0;
        const infectedCount = cluster.infected_member_count || 0;
        const memberIds = cluster.member_ids || [];
        const isInfected = cluster.is_infected || infectedCount > 0 || riskScore >= 50;

        // Determine risk factors
        const keyFactors: string[] = [];
        if (wideBridges.length > 0) keyFactors.push(`${wideBridges.length} wide bridge(s)`);
        if (infectedCount > 0) keyFactors.push(`${infectedCount} high-risk members`);
        if ((cluster.redundancy_score || 0) > 0.5) keyFactors.push("High risky neighbor exposure");
        const chronicMembers = members.filter((m) => m.safety_state === "chronic").length;
        if (chronicMembers > 0) keyFactors.push(`${chronicMembers} chronic offender(s)`);

        return {
          id: cluster.id,
          cluster_name: cluster.cluster_name,
          cluster_type: cluster.cluster_type,
          member_count: memberIds.length || members.length,
          risk_score: riskScore,
          redundancy_score: cluster.redundancy_score || 0,
          infected_count: infectedCount,
          site_id: siteByCluster[cluster.id] || null,
          site_name: undefined,
          created_at: cluster.created_at,
          members,
          wide_bridges: wideBridges,
          key_risk_factors: keyFactors,
          is_infected: isInfected,
        };
      });

      setClusters(diagnostics);

      // Calculate stats
      const total = diagnostics.length;
      const infected = diagnostics.filter((c) => c.is_infected).length;
      const highRisk = diagnostics.filter((c) => c.risk_score >= 60).length;
      const avgScore = total > 0 ? diagnostics.reduce((sum, c) => sum + c.risk_score, 0) / total : 0;
      const totalBridges = diagnostics.reduce((sum, c) => sum + c.wide_bridges.length, 0);

      setStats({
        totalClusters: total,
        infectedClusters: infected,
        highRiskClusters: highRisk,
        avgRiskScore: Math.round(avgScore),
        totalWideBridges: totalBridges,
      });
    } catch (error) {
      console.error("Error fetching cluster diagnostics:", error);
      toast({ title: "Error", description: "Failed to load cluster diagnostics", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const detectClusters = async () => {
    setDetectingClusters(true);
    try {
      const { data, error } = await supabase.functions.invoke("detect-worker-clusters", {
        body: { siteId: selectedSite !== "all" ? selectedSite : undefined },
      });

      if (error) throw error;

      toast({
        title: "Clusters Detected",
        description: `Found ${data?.clusters?.length || 0} clusters with ${data?.wideBridges?.length || 0} wide bridges`,
      });

      await fetchClusterDiagnostics();
    } catch (error) {
      console.error("Error detecting clusters:", error);
      toast({ title: "Error", description: "Failed to detect clusters", variant: "destructive" });
    } finally {
      setDetectingClusters(false);
    }
  };

  const handleGenerateRecommendations = async () => {
    if (!companyId) {
      toast({ title: "Error", description: "Company context not loaded", variant: "destructive" });
      return;
    }
    
    setGeneratingRecommendations(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-intervention-recommendations", {
        body: {
          targetType: "cluster",
          generateForAll: true,
          companyId: companyId,
          siteId: selectedSite !== "all" ? selectedSite : undefined,
        },
      });

      if (error) throw error;

      toast({
        title: "Recommendations Generated",
        description: `Created ${data?.recommendationsCreated || 0} team intervention suggestions`,
      });

      // Navigate to unified Recommendations Hub
      navigate("/admin?tab=recommendations");
    } catch (error: any) {
      console.error("Error generating recommendations:", error);
      toast({ 
        title: "Error", 
        description: error?.message || "Failed to generate recommendations", 
        variant: "destructive" 
      });
    } finally {
      setGeneratingRecommendations(false);
    }
  };

  const getRiskBadge = (score: number) => {
    if (score >= 60) return <Badge variant="destructive">High Risk</Badge>;
    if (score >= 40) return <Badge className="bg-warning text-warning-foreground">Medium</Badge>;
    return <Badge className="bg-success text-success-foreground">Low</Badge>;
  };

  const getMemberStateBadge = (state: string) => {
    switch (state) {
      case "chronic":
        return <Badge className="bg-red-600 text-white text-xs">Chronic</Badge>;
      case "high_risk":
        return <Badge className="bg-orange-500 text-white text-xs">High Risk</Badge>;
      case "at_risk":
        return <Badge className="bg-warning text-warning-foreground text-xs">At Risk</Badge>;
      default:
        return <Badge className="bg-success text-success-foreground text-xs">Safe</Badge>;
    }
  };

  const filteredClusters = clusters.filter((c) => {
    const matchesSearch =
      (c.cluster_name?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (c.cluster_type?.toLowerCase() || "").includes(searchQuery.toLowerCase());

    let matchesState = true;
    if (stateFilter === "infected") matchesState = c.is_infected;
    else if (stateFilter === "high_risk") matchesState = c.risk_score >= 60;
    else if (stateFilter === "healthy") matchesState = !c.is_infected && c.risk_score < 40;

    return matchesSearch && matchesState;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-2">Analisis Kelompok Kerja</h2>
          <p className="text-muted-foreground">
            Lihat tim mana yang butuh perhatian dan kebiasaan apa yang bisa menular antar tim
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {!propSiteId && <SiteFilter value={selectedSite} onChange={setSelectedSite} />}
          <Button onClick={detectClusters} disabled={detectingClusters} variant="outline" className="gap-2">
            <RefreshCw className={cn("h-4 w-4", detectingClusters && "animate-spin")} />
            Deteksi Tim
          </Button>
          <Button onClick={handleGenerateRecommendations} disabled={generatingRecommendations} className="gap-2">
            <Sparkles className={cn("h-4 w-4", generatingRecommendations && "animate-spin")} />
            Buat Rekomendasi
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            Total Tim
          </div>
          <div className="text-2xl font-bold mt-1">{stats?.totalClusters}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <TriangleAlert className="h-4 w-4 text-destructive" />
            Perlu Perhatian
          </div>
          <div className="text-2xl font-bold mt-1 text-destructive">{stats?.infectedClusters}</div>
          <p className="text-xs text-muted-foreground">Ada anggota bermasalah</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Activity className="h-4 w-4 text-warning" />
            Risiko Tinggi
          </div>
          <div className="text-2xl font-bold mt-1 text-warning">{stats?.highRiskClusters}</div>
          <p className="text-xs text-muted-foreground">Skor ≥ 60%</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Target className="h-4 w-4" />
            Rata-rata Risiko
          </div>
          <div className={cn("text-2xl font-bold mt-1", 
            (stats?.avgRiskScore || 0) >= 60 ? "text-destructive" : 
            (stats?.avgRiskScore || 0) >= 40 ? "text-warning" : "text-success"
          )}>
            {stats?.avgRiskScore}%
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link2 className="h-4 w-4 text-purple-400" />
            Penghubung Tim
          </div>
          <div className="text-2xl font-bold mt-1 text-purple-400">{stats?.totalWideBridges}</div>
          <p className="text-xs text-muted-foreground">Bisa sebarkan kebiasaan</p>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by cluster name or type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            {(
              [
                { key: "all", label: "Semua" },
                { key: "infected", label: "Bermasalah" },
                { key: "high_risk", label: "Risiko Tinggi" },
                { key: "healthy", label: "Aman" },
              ] as const
            ).map((filter) => (
              <Button
                key={filter.key}
                variant={stateFilter === filter.key ? "default" : "outline"}
                size="sm"
                onClick={() => setStateFilter(filter.key)}
                className={cn(
                  filter.key === "infected" && stateFilter === filter.key && "bg-destructive hover:bg-destructive/90",
                  filter.key === "high_risk" && stateFilter === filter.key && "bg-warning hover:bg-warning/90",
                  filter.key === "healthy" && stateFilter === filter.key && "bg-success hover:bg-success/90",
                )}
              >
                {filter.label}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* Cluster List */}
      <div className="space-y-3">
        {filteredClusters.map((cluster) => (
          <Card
            key={cluster.id}
            className={cn(
              "transition-all hover:shadow-md cursor-pointer",
              cluster.risk_score >= 60 && "border-l-4 border-l-destructive",
              cluster.risk_score >= 40 && cluster.risk_score < 60 && "border-l-4 border-l-warning",
              cluster.risk_score < 40 && "border-l-4 border-l-success",
              cluster.wide_bridges.length > 0 && "ring-2 ring-purple-500/50",
            )}
            onClick={() => setExpandedCluster(expandedCluster === cluster.id ? null : cluster.id)}
          >
            <CardContent className="p-4">
              {/* Main Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* Risk Score */}
                  <div className="flex flex-col items-center gap-1">
                    <div className={cn(
                      "text-2xl font-bold",
                      cluster.risk_score >= 60 ? "text-destructive" : 
                      cluster.risk_score >= 40 ? "text-warning" : "text-success"
                    )}>
                      {cluster.risk_score}%
                    </div>
                    <span className="text-xs text-muted-foreground">risk</span>
                  </div>

                  {/* Cluster Info */}
                  <div>
                    <p className="font-semibold flex items-center gap-2">
                      {cluster.cluster_name || `Cluster ${cluster.id.slice(0, 8)}`}
                      {cluster.is_infected && (
                        <Badge variant="destructive" className="gap-1">
                          <TriangleAlert className="h-3 w-3" />
                          Perlu Perhatian
                        </Badge>
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {cluster.member_count} anggota • {cluster.cluster_type || "Kelompok kerja"}
                      {cluster.site_name && ` • ${cluster.site_name}`}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {getRiskBadge(cluster.risk_score)}
                  {cluster.wide_bridges.length > 0 && (
                    <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 gap-1">
                      <Link2 className="h-3 w-3" />
                      {cluster.wide_bridges.length} bridge{cluster.wide_bridges.length > 1 ? "s" : ""}
                    </Badge>
                  )}
                  {expandedCluster === cluster.id ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedCluster === cluster.id && (
                <div className="mt-4 pt-4 border-t space-y-4">
                  {/* PLAIN LANGUAGE SUMMARY */}
                  <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-4">
                    <p className="text-sm font-medium mb-3 flex items-center gap-2 text-primary">
                      <Lightbulb className="h-4 w-4" />
                      Mengapa Tim Ini Perlu Perhatian?
                    </p>
                    <ul className="space-y-2">
                      {generateClusterSummary(cluster).map((point, i) => (
                        <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                          <span className="text-primary mt-0.5">•</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {cluster.risk_score < 30 && (
                      <p className="text-sm text-success flex items-center gap-2 mt-3">
                        <Shield className="h-4 w-4" />
                        Tim ini dalam kondisi baik
                      </p>
                    )}
                  </div>
                  
                  {/* Wide Bridges Highlight - Plain language */}
                  {cluster.wide_bridges.length > 0 && (
                    <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                      <p className="text-sm font-medium mb-2 flex items-center gap-2 text-purple-400">
                        <Link2 className="h-4 w-4" />
                        Anggota dengan Pengaruh Luas
                      </p>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {cluster.wide_bridges.map((bridge) => (
                          <Badge key={bridge.id} className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                            {bridge.display_name}
                          </Badge>
                        ))}
                      </div>
                      <p className="text-xs text-purple-400/70">
                        Mereka punya banyak teman di tim lain. Jika kebiasaannya tidak diperbaiki, bisa cepat menular ke banyak orang.
                      </p>
                    </div>
                  )}
                  
                  {/* TECHNICAL DETAILS - Hidden by default */}
                  <Collapsible 
                    open={showTechnicalDetails === cluster.id}
                    onOpenChange={(open) => setShowTechnicalDetails(open ? cluster.id : null)}
                  >
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="w-full justify-between text-muted-foreground">
                        <span className="flex items-center gap-2">
                          <Eye className="h-4 w-4" />
                          Lihat Detail Teknis
                        </span>
                        <ChevronDown className={cn("h-4 w-4 transition-transform", showTechnicalDetails === cluster.id && "rotate-180")} />
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3 space-y-4">
                      {/* Metrics Row */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground">Members</p>
                          <p className="text-xl font-bold">{cluster.member_count}</p>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground">High Risk Members</p>
                          <p className={cn("text-xl font-bold", cluster.infected_count > 0 && "text-destructive")}>
                            {cluster.infected_count}
                          </p>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground">Redundancy Score</p>
                          <p className={cn("text-xl font-bold", cluster.redundancy_score > 0.5 && "text-warning")}>
                            {(cluster.redundancy_score * 100).toFixed(0)}%
                          </p>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground">Wide Bridges</p>
                          <p className={cn("text-xl font-bold", cluster.wide_bridges.length > 0 && "text-purple-400")}>
                            {cluster.wide_bridges.length}
                          </p>
                        </div>
                      </div>

                      {/* Key Risk Factors */}
                      <div>
                        <p className="text-sm font-medium mb-2 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-warning" />
                          Key Risk Factors
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {cluster.key_risk_factors.length > 0 ? (
                            cluster.key_risk_factors.map((factor, i) => (
                              <Badge key={i} variant="outline" className="bg-muted/50">
                                {factor}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-sm text-muted-foreground">No significant risk factors</span>
                          )}
                        </div>
                      </div>

                      {/* Members List */}
                      <div>
                        <p className="text-sm font-medium mb-2 flex items-center gap-2">
                          <Users className="h-4 w-4 text-primary" />
                          Team Members ({cluster.members.length})
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                          {cluster.members.slice(0, 8).map((member) => (
                            <div
                              key={member.id}
                              className={cn(
                                "bg-muted/30 rounded-lg p-2 text-sm",
                                member.is_wide_bridge && "ring-1 ring-purple-500/50"
                              )}
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium truncate">{member.display_name}</span>
                                {member.is_wide_bridge && <Link2 className="h-3 w-3 text-purple-400" />}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                {getMemberStateBadge(member.safety_state)}
                                <span className="text-xs text-muted-foreground">{member.current_risk_score}%</span>
                              </div>
                            </div>
                          ))}
                          {cluster.members.length > 8 && (
                            <div className="bg-muted/30 rounded-lg p-2 text-sm flex items-center justify-center">
                              <span className="text-muted-foreground">+{cluster.members.length - 8} more</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Metrics Row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-muted/30 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground">Members</p>
                      <p className="text-xl font-bold">{cluster.member_count}</p>
                    </div>
                    <div className="bg-muted/30 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground">Infected</p>
                      <p className={cn("text-xl font-bold", cluster.infected_count > 0 && "text-destructive")}>
                        {cluster.infected_count}
                      </p>
                    </div>
                    <div className="bg-muted/30 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground">Redundancy Score</p>
                      <p className={cn("text-xl font-bold", cluster.redundancy_score > 0.5 && "text-warning")}>
                        {(cluster.redundancy_score * 100).toFixed(0)}%
                      </p>
                    </div>
                    <div className="bg-muted/30 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground">Wide Bridges</p>
                      <p className={cn("text-xl font-bold", cluster.wide_bridges.length > 0 && "text-purple-400")}>
                        {cluster.wide_bridges.length}
                      </p>
                    </div>
                  </div>

                  {/* Key Risk Factors */}
                  <div>
                    <p className="text-sm font-medium mb-2 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-warning" />
                      Key Risk Factors
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {cluster.key_risk_factors.length > 0 ? (
                        cluster.key_risk_factors.map((factor, i) => (
                          <Badge key={i} variant="outline" className="bg-muted/50">
                            {factor}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-sm text-muted-foreground">No significant risk factors</span>
                      )}
                    </div>
                  </div>

                  {/* Members List */}
                  <div>
                    <p className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Users className="h-4 w-4 text-primary" />
                      Team Members ({cluster.members.length})
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {cluster.members.slice(0, 8).map((member) => (
                        <div
                          key={member.id}
                          className={cn(
                            "bg-muted/30 rounded-lg p-2 text-sm",
                            member.is_wide_bridge && "ring-1 ring-purple-500/50"
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium truncate">{member.display_name}</span>
                            {member.is_wide_bridge && <Link2 className="h-3 w-3 text-purple-400" />}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {getMemberStateBadge(member.safety_state)}
                            <span className="text-xs text-muted-foreground">{member.current_risk_score}%</span>
                          </div>
                        </div>
                      ))}
                      {cluster.members.length > 8 && (
                        <div className="bg-muted/30 rounded-lg p-2 text-sm flex items-center justify-center">
                          <span className="text-muted-foreground">+{cluster.members.length - 8} more</span>
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {filteredClusters.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              {clusters.length === 0 ? (
                <div className="space-y-3">
                  <Network className="h-12 w-12 mx-auto text-muted-foreground/50" />
                  <p>No clusters detected yet</p>
                  <Button onClick={detectClusters} disabled={detectingClusters}>
                    <RefreshCw className={cn("h-4 w-4 mr-2", detectingClusters && "animate-spin")} />
                    Detect Clusters Now
                  </Button>
                </div>
              ) : (
                "No clusters match your filters"
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ClusterDiagnostics;
